
import React, { useState } from "react";
import Header from "@/components/Header";
import FeatureRoadmap from "@/components/knowledge-base/FeatureRoadmap";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ExternalLink, Lightbulb } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Helmet } from "react-helmet-async";
import { useIsMobile } from "@/hooks/use-mobile";

const Roadmap = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // State for feature filtering
  const [viewMode, setViewMode] = useState<"all" | "planned" | "implemented">("all");
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  const categories = [
    { value: "core", label: "Core Features" },
    { value: "filtering", label: "Filtering" },
    { value: "metrics", label: "Metrics" },
    { value: "display", label: "Display" },
    { value: "tutorials", label: "Tutorials" },
    { value: "user", label: "User" },
    { value: "parts", label: "Parts" },
    { value: "knowledge-base", label: "Knowledge Base" },
    { value: "interactive", label: "Interactive" },
    { value: "community", label: "Community" },
    { value: "accessibility", label: "Accessibility" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>3DP Hub Roadmap - Current and Planned Features for 3D Printing Resources</title>
        <meta name="description" content="Explore 3DP Hub's roadmap of current and planned features, including part catalogs, troubleshooting resources, and community tools for 3D printing enthusiasts." />
        <meta name="keywords" content="3D printing, roadmap, 3D printer upgrades, troubleshooting, 3D printing community, feature planning, 3D printer resources" />
        <link rel="canonical" href="https://3dphub.net/roadmap" />
        <meta property="og:title" content="3DP Hub Roadmap - Current and Planned Features" />
        <meta property="og:description" content="Discover what's available now and what's coming soon to 3DP Hub, the ultimate resource for 3D printer owners." />
        <meta property="og:url" content="https://3dphub.net/roadmap" />
        <meta property="og:type" content="website" />
      </Helmet>
      <Header 
        pageName="Roadmap"
      />
      
      <div className="container max-w-6xl py-6 pt-[64px]">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">3DP Hub Roadmap</h1>
            <p className="text-muted-foreground">
              Current and planned features for the 3DP Hub platform
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" className="w-full md:w-auto h-10 border border-gray-300 dark:border-gray-600 bg-background hover:bg-accent hover:text-accent-foreground" asChild>
              <Link to="/suggest-feature" className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4" />
                Suggest a feature
              </Link>
            </Button>
            <Button variant="ghost" className="w-full md:w-auto h-10 border border-gray-300 dark:border-gray-600 bg-background hover:bg-accent hover:text-accent-foreground" asChild>
              <a href="https://3dphub.net/donate" className="flex items-center gap-2">
                {isMobile ? "Donate" : "Help build the platform"}
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
        
        <div className="mb-8 bg-blue-50 dark:bg-blue-950/20 rounded-lg p-6 border border-border">
          <h2 className="text-xl font-semibold mb-4">About 3DP Hub</h2>
          <div className="prose dark:prose-invert">
            <p className="text-muted-foreground mb-4">
              3DP Hub is the ultimate resource for 3D printer owners seeking high-quality guides, troubleshooting help, technical data, replacement parts, and performance upgrades. Our mission is to create a comprehensive knowledge base that makes 3D printing more accessible and successful for enthusiasts of all skill levels.
            </p>
            <p className="text-muted-foreground mb-4">
              Whether you're looking to fix a technical issue, upgrade your printer, or learn best practices, our growing library of community-contributed resources aims to be your one-stop destination for all things 3D printing.
            </p>
          </div>
        </div>
        
        <div className="space-y-6">
          <div className="flex justify-end mb-4">
            <ToggleGroup type="single" value={viewMode} onValueChange={(value) => value && setViewMode(value as "all" | "planned" | "implemented")}>
              <ToggleGroupItem value="all">All Features</ToggleGroupItem>
              <ToggleGroupItem value="implemented" className="data-[state=on]:bg-green-800 data-[state=on]:text-white">Implemented</ToggleGroupItem>
              <ToggleGroupItem value="planned" className="data-[state=on]:bg-gray-800 data-[state=on]:text-black">Planned</ToggleGroupItem>
            </ToggleGroup>
          </div>
          
          <FeatureRoadmap 
            showImplemented={viewMode === "all" || viewMode === "implemented"} 
            showPlanned={viewMode === "all" || viewMode === "planned"}
            categoryFilter={null}
          />
        </div>
      </div>
      
      <footer className="bg-card border-t border-border py-6 mt-auto">
        <div className="container">
          <p className="text-sm text-center text-muted-foreground">
            3DP Hub | The ultimate directory for 3D printer parts and upgrades
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Roadmap;
